import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  conversationsApi,
  useAddConversationMutation,
  useEditConversationMutation
} from "../../features/conversations/conversationsApi";
import { useGetUserQuery } from "../../features/users/usersApi";
import debounce from "../../utils/debounce.js";
import isValidEmail from "../../utils/isValidEmail.js";
import Error from "../ui/Error";

export default function Modal({ open, control }) {
  const [disabled, setDisabled] = useState(false);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [skip, setSkip] = useState(true);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [conversation, setConversation] = useState(undefined);
  const [responseErr, setResponseErr] = useState("");

  const { data: participant } = useGetUserQuery(to, { skip });
  const [
    addConversation,
    {
      isSuccess: isAddConversationSuccess,
      isLoading: isAddConversationLoading,
    },
  ] = useAddConversationMutation();
  const [
    editConversation,
    {
      isSuccess: isEditConversationSuccess,
      isLoading: isEditConversationLoading,
    },
  ] = useEditConversationMutation();

  useEffect(() => {
    if (participant?.length > 0 && participant[0].email !== user?.email) {
      dispatch(
        conversationsApi.endpoints.getConversation.initiate({
          userEmail: user?.email,
          partnerEmail: to,
        })
      )
        .unwrap()
        .then((data) => setConversation(data))
        .catch((err) => setResponseErr("There is a Problem!"));
    }
  }, [conversation, user, to, dispatch, participant]);

  useEffect(() => {
    if (isAddConversationSuccess || isEditConversationSuccess) {
      control();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddConversationSuccess, isEditConversationSuccess]);

  // disable modal button
  useEffect(() => {
    if (isAddConversationLoading || isEditConversationLoading) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddConversationLoading, isEditConversationLoading]);

  const doSearch = (e) => {
    const email = e.target.value;

    if (isValidEmail(email)) {
      setSkip(false);
      setTo(email);
    }
  };

  const handleSearch = debounce(doSearch);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (conversation?.length > 0) {
      editConversation({
        sender: user?.email,
        id: conversation[0].id,
        data: {
          participants: `${user.email}-${participant[0].email}`,
          users: [user, participant[0]],
          message,
          timestamp: new Date().getTime(),
        },
      });
    } else if (conversation?.length === 0) {
      addConversation({
        sender: user?.email,
        data: {
          participants: `${user.email}-${participant[0].email}`,
          users: [user, participant[0]],
          message,
          timestamp: new Date().getTime(),
        },
      });
    }
  };

  return (
    open && (
      <>
        <div
          onClick={control}
          className="fixed w-full h-full inset-0 z-10 bg-black/50 cursor-pointer"
        ></div>
        <div className="rounded w-[400px] lg:w-[600px] space-y-8 bg-white p-10 absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Send message
          </h2>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <input type="hidden" name="remember" value="true" />
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="to" className="sr-only">
                  To
                </label>
                <input
                  id="to"
                  name="to"
                  type="email"
                  onChange={handleSearch}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-violet-500 focus:border-violet-500 focus:z-10 sm:text-sm"
                  placeholder="Send to"
                />
              </div>
              <div>
                <label htmlFor="message" className="sr-only">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-violet-500 focus:border-violet-500 focus:z-10 sm:text-sm"
                  placeholder="Message"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  disabled ? "bg-violet-500" : "bg-violet-600"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
                disabled={disabled}
              >
                Send Message
              </button>
            </div>

            {participant?.length === 0 && (
              <Error message="This user does not exist!" />
            )}

            {participant?.length > 0 &&
              participant[0].email === user?.email && (
                <Error message="You can not send message to yourself!" />
              )}

            {responseErr && <Error message={responseErr} />}
          </form>
        </div>
      </>
    )
  );
}
